
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip } from 'recharts';
import { pillarConfig } from '@/data/AITrainingTransformationData';

interface ScoreChartProps {
  scores: Record<string, { score: number; maxScore: number }>;
  /** Override colors per axis name (used by training-transformation, axes from DB). */
  axisColors?: Record<string, string>;
}

const labelFormatting: Record<string, string> = {
  // Legacy 5-pillar labels
  "Stratégie": "Stratégie",
  "Déploiement": "Déploiement",
  "Production": "Production",
  "Pilotage": "Pilotage",
  "Formation et change": "Formation\net change",
  // 9 training-transformation axes (single line by default)
  "Offre": "Offre",
  "Commercial": "Commercial",
  "Organisation": "Organisation",
  "Processus": "Processus",
  "Ingénierie": "Ingénierie",
  "Qualité": "Qualité",
  "Outils": "Outils",
  "Change": "Change",
};

export const ScoreChart: React.FC<ScoreChartProps> = ({ scores, axisColors }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const update = () => {
      const width = containerRef.current?.getBoundingClientRect().width
        ?? window.innerWidth;
      setIsMobile(width < 640);
      setContainerWidth(width);
    };

    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const ro = new ResizeObserver(() => update());
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const data = useMemo(
    () =>
      Object.entries(scores)
        .filter(([category]) => category !== 'Profil entreprise')
        .map(([category, d]) => {
          const safeMaxScore = Number.isFinite(d.maxScore) && d.maxScore > 0 ? d.maxScore : 0;
          const safeScore = Number.isFinite(d.score) ? d.score : 0;
          const normalizedScore = safeMaxScore > 0 ? Math.round((safeScore / safeMaxScore) * 100) : 0;

          return {
            category: labelFormatting[category] || category,
            rawCategory: category,
            score: Number.isFinite(normalizedScore) ? normalizedScore : 0,
            fullScore: 100,
          };
        })
        .filter((item) => Number.isFinite(item.score)),
    [scores],
  );

  const outerRadius = isMobile ? '58%' : '62%';
  const chartMargin = isMobile
    ? { top: 24, right: 28, bottom: 24, left: 28 }
    : { top: 24, right: 60, bottom: 24, left: 60 };

  // Hauteur FIXE en pixels sur le parent direct du ResponsiveContainer.
  // Recharts en a besoin pour mesurer correctement dès le premier paint.
  const fixedHeight = 380;

  if (data.length === 0) {
    return (
      <div
        ref={containerRef}
        style={{ width: '100%', height: fixedHeight }}
        className="flex items-center justify-center text-sm text-gray-400"
      >
        Calcul du graphique…
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: fixedHeight }}>
      <RadarChart
        width={containerWidth || 600}
        height={380}
        cx="50%"
        cy="50%"
        outerRadius={outerRadius}
        data={data}
        margin={chartMargin}
      >
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="category"
            tick={({ payload, x, y, textAnchor, index }) => {
              const lines = String(payload.value).split('\n');
              const rawCat = data[index]?.rawCategory || '';
              const fillColor =
                axisColors?.[rawCat] || pillarConfig[rawCat]?.color || '#374151';
              return (
                <text
                  x={x}
                  y={y}
                  textAnchor={textAnchor}
                  fill={fillColor}
                  fontSize={10}
                  fontWeight={600}
                  dominantBaseline="central"
                  className="md:text-xs"
                >
                  {lines.map((line: string, i: number) => (
                    <tspan key={i} x={x} dy={i === 0 ? 0 : 11}>
                      {line}
                    </tspan>
                  ))}
                </text>
              );
            }}
            tickLine={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="Score"
            dataKey="score"
            stroke="#D7398B"
            fill="#D7398B"
            fillOpacity={0.3}
            strokeWidth={2}
          />
          <Tooltip formatter={(value: number) => [`${value}%`, 'Score']} />
      </RadarChart>
    </div>
  );
};
